import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Card } from 'react-bootstrap';
import {
  Divider,
  Box,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Container,
} from '@mui/material';

import { getLeaseByIdForCompletePreview } from '../TenantForm/Helpers/api';
import { ThemeWrapper } from '../LeaseProcess/Flow/StepperComponents/sign-lease/utils';
import { Step1, Step2, Step3, Step4, Step5, Step6 } from '../LeaseProcess/Flow/StepperComponents/lease/components/steps/3-PreviewLeaseByManager/Steps';
import '../TenantForm/Helpers/leases.scss';

const Footer = ({ lease }) => {
  const landlordName = lease?.manager
    ? `${lease.manager.first_name || ''} ${lease.manager.last_name || ''}`.trim()
    : '';
  const landlordInitials = landlordName
    ? landlordName.split(' ').map((w) => w.charAt(0).toUpperCase()).join('')
    : 'N/A';

  const tenantInitials = lease?.tenants?.length > 0
    ? lease.tenants
        .map((t) => t.name)
        .filter(Boolean)
        .map((name) => name.split(' ').map((w) => w.charAt(0).toUpperCase()).join(''))
        .join(', ')
    : 'N/A';

  return (
    <Box className="lease-form-footer" display="flex" justifyContent="flex-end" alignItems="center">
      <Box textAlign="center" mr={2}>
        <Typography variant="caption" fontSize="0.7rem">INITIALS</Typography>
      </Box>
      <Table size="small" sx={{ width: '120px', border: '1px solid', borderColor: 'grey.500' }}>
        <TableBody>
          <TableRow>
            <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'grey.500', fontSize: '0.8rem', p: 0.5 }}>Landlord</TableCell>
            <TableCell align="center" sx={{ fontSize: '0.8rem', p: 0.5 }}>Tenant(s)</TableCell>
          </TableRow>
          <TableRow>
            <TableCell align="center" sx={{ borderRight: '1px solid', borderColor: 'grey.500', fontSize: '0.9rem', p: 0.5 }}>{landlordInitials}</TableCell>
            <TableCell align="center" sx={{ fontSize: '0.9rem', p: 0.5 }}>{tenantInitials}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

const LeasePrintView = () => {
  const { leaseId } = useParams();
  const location = useLocation();
  const [leaseData, setLeaseData] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    if (token) {
      try { window.localStorage.setItem('accessToken', token); } catch { /* ignore */ }
    }

    const run = async () => {
      try {
        if (!leaseId) throw new Error('Lease ID is required');
        const response = await getLeaseByIdForCompletePreview(leaseId);
        if (!response?.success) {
          throw new Error(response?.message || 'Failed to fetch lease');
        }
        setLeaseData(response.data);
      } catch (err) {
        const msg = err?.message || 'Error fetching lease';
        setErrorMessage(msg);
        try { window.__LEASE_PRINT_ERROR = msg; } catch { /* ignore */ }
      }
    };

    run();
  }, [leaseId, location.search]);

  useEffect(() => {
    if (!leaseData) return;
    let cancelled = false;

    const waitForImages = () => {
      const imgs = Array.from(document.querySelectorAll('img'));
      if (imgs.length === 0) return Promise.resolve();
      return Promise.all(imgs.map((img) => {
        if (img.complete && img.naturalWidth > 0) return Promise.resolve();
        return new Promise((resolve) => {
          let done = false;
          const finish = () => { if (!done) { done = true; resolve(); } };
          img.addEventListener('load', finish, { once: true });
          img.addEventListener('error', finish, { once: true });
          setTimeout(finish, 8000);
        });
      }));
    };

    const signalReady = async () => {
      await new Promise((r) => requestAnimationFrame(r));
      await new Promise((r) => requestAnimationFrame(r));
      await waitForImages();
      try {
        if (document.fonts && document.fonts.ready) await document.fonts.ready;
      } catch { /* ignore */ }
      await new Promise((r) => requestAnimationFrame(r));
      if (cancelled) return;
      try { window.__LEASE_PRINT_READY = true; } catch { /* ignore */ }
      try { console.log('__LEASE_PRINT_READY set'); } catch { /* ignore */ }
    };

    signalReady();
    return () => { cancelled = true; };
  }, [leaseData]);

  if (errorMessage) {
    return (
      <Box p={4}>
        <Typography color="error">Failed to load lease preview: {errorMessage}</Typography>
      </Box>
    );
  }

  if (!leaseData) {
    return (
      <Box p={4}>
        <Typography>Loading lease...</Typography>
      </Box>
    );
  }

  const steps = [
    <Step1 lease={leaseData} />,
    <Step2 lease={leaseData} />,
    <Step3 lease={leaseData} />,
    <Step4 lease={leaseData} />,
    <Step5 lease={leaseData} />,
    <Step6 lease={leaseData} />,
  ];

  return (
    <ThemeWrapper>
      <style>{`
        html, body, #root { background: #ffffff !important; margin: 0 !important; padding: 0 !important; }
        .lease-print-page, .lease-print-container, .lease-form-existing, .lease-form { background: #ffffff !important; }
        .lease-print-page, .lease-print-page * { visibility: visible !important; }
        @media print {
          body, body *, .lease-print-page, .lease-print-page * { visibility: visible !important; }
          .lease-step { break-inside: avoid; page-break-inside: avoid; }
          .lease-step + .lease-step { page-break-before: always; }
        }
      `}</style>
      <Box className="lease-print-page lease-form" sx={{ backgroundColor: '#ffffff', pt: 0, pb: 0 }}>
        <Container maxWidth={false} disableGutters sx={{ mt: 0, mb: 0, p: 0 }} className="lease-print-container">
          <Card className="p-3 lease-form-existing" style={{ backgroundColor: 'white', border: 'none', boxShadow: 'none' }}>
            <Card.Body>
              <div>
                {steps.map((step, index) => (
                  <div key={index} className="mb-4 lease-step">
                    {step}
                    <Footer lease={leaseData} />
                    {index < steps.length - 1 && (
                      <Divider sx={{ my: 4, borderColor: 'gray' }} />
                    )}
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
        </Container>
      </Box>
    </ThemeWrapper>
  );
};

export { LeasePrintView };
export default LeasePrintView;
